import React, { useEffect } from 'react';
import { Typography, Row, Col, Card, Space, Button, Divider } from 'antd';
import { ArrowLeftOutlined, MailOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import './ContactPage.css';

const { Title, Text, Paragraph } = Typography;

const ContactPage: React.FC = () => {
  const navigate = useNavigate();

  // Set page metadata
  useEffect(() => {
    document.title = 'Contact | SNS Intelligence';
    
    // Update or create meta description
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.setAttribute('name', 'description');
      document.head.appendChild(metaDescription);
    }
    metaDescription.setAttribute('content', 'Contact SNS Intelligence for product, licensing, or partnership inquiries.');
  }, []);

  return (
    <div className="contact-page">
      {/* Navigation */}
      <nav className="contact-navbar">
        <div className="nav-container">
          <div className="nav-left">
            <div
              className="nav-logo"
              role="button"
              tabIndex={0}
              onClick={() => navigate('/')}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  navigate('/');
                }
              }}
            >
              <img src="/sns-focus-logo.png" alt="SNS Intelligence" />
            </div>
            <div className="nav-links">
              <a href="/platform">Platform</a>
              <a href="/markets">Markets</a>
              <a href="/pulse">Pulse</a>
              <a href="/press">Press</a>
            </div>
          </div>
          <div className="nav-actions">
            <Button type="primary" onClick={() => navigate('/login')}>Open Portal</Button>
          </div>
        </div>
      </nav>

      {/* Back to Home Button */}
      <div className="back-to-home-section">
        <Button
          type="default"
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/')}
          className="back-to-home-button"
        >
          Back to Home
        </Button>
      </div>

      <div className="contact-container">
        <Row justify="center">
          <Col xs={24} md={20} lg={16}>
            <div className="contact-header">
              <Title level={1} className="contact-title">
                Contact Us
              </Title>
            </div>

            <Card className="contact-card">
              <Space direction="vertical" size="large" style={{ width: '100%' }}>
                <Paragraph>
                  For product inquiries, licensing, partnerships, or general questions, please reach out to us.
                </Paragraph>

                <div style={{ textAlign: 'center', padding: '24px 0' }}>
                  <MailOutlined style={{ fontSize: '24px', color: '#262626', marginBottom: '16px' }} />
                  <Paragraph style={{ fontSize: '16px', marginBottom: '8px' }}>
                    <a href="mailto:info@snsintel.com" className="contact-email-link">
                      info@snsintel.com
                    </a>
                  </Paragraph>
                </div>

                <Paragraph>
                  We aim to respond within 1–2 business days.
                </Paragraph>

                <Paragraph>
                  If you're an existing customer, please include your company name and the email used to sign in.
                </Paragraph>
              </Space>
            </Card>
          </Col>
        </Row>
      </div>

      {/* Footer */}
      <footer className="contact-footer">
        <div className="section-container">
          <div className="footer-content">
            <div className="footer-logo-section">
              <div className="footer-logo">
                <img src="/sns-focus-logo.png" alt="SNS Intelligence" />
              </div>
            </div>
            <div className="footer-links-section">
              <div className="footer-column">
                <Title level={5}>Product</Title>
                <div className="footer-links">
                  <a href="/platform">Platform</a>
                  <a href="/data-coverage">Data Coverage</a>
                  <a href="/markets">Markets</a>
                  <a href="/pulse">Pulse</a>
                </div>
              </div>
              <div className="footer-column">
                <Title level={5}>Company</Title>
                <div className="footer-links">
                  <a href="/about">About</a>
                  <a href="/contact">Contact</a>
                  <a href="/press">Press</a>
                </div>
              </div>
            </div>
          </div>
          <Divider />
          <div className="footer-bottom">
            <Text>&copy; 2026 SNS Intelligence. All rights reserved.</Text>
            <div className="footer-legal">
              <a href="/privacy-policy">Privacy Policy</a>
              <a href="/terms-of-service">Terms of Service</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ContactPage;
